import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import i18n from '../i18n';

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

// Available subscription tiers for testing
export const SUBSCRIPTION_TIERS = {
  full: { name: 'Full Platform', description: 'All features enabled', nameKey: 'modules.full', descKey: 'modules.allFeatures' },
  standalone_timeclock: { name: 'TimeClock Pro', description: 'Time tracking only', nameKey: 'modules.standalone_timeclock', descKey: 'modules.timeTrackingOnly' },
  standalone_accounting: { name: 'PayTrack', description: 'Accounting & rate sheets', nameKey: 'modules.standalone_accounting', descKey: 'modules.accountingOnly' },
  none: { name: 'Free/Trial', description: 'Limited features', nameKey: 'modules.none', descKey: 'modules.limitedFeatures' }
};

// Feature definitions for each subscription type
const SUBSCRIPTION_FEATURES = {
  // Full platform - all features
  full: {
    dashboard: true,
    shipments: true,
    dispatch: true,
    drivers: true,
    tracking: true,
    messaging: true,
    accounting: true,
    accounting_drivers: true,
    accounting_rates: true,
    accounting_worksheets: true,
    hr: true,
    clock: true,
    geofencing: true,
    pto: true,
    master_tables: true,
    users: true,
    ai_parsing: true,
    system: true,
    terminal_integration: true,
    gate_schedules: true,
    pod: true,
    otr: true,
    load_board: true,
    hos: true,
    freight_margin_ai: true,
    compliance: true,
    broker_tms: true,
    roadside_rescue: true,
    dnd_disputes: true,
    equipment_issues: true,
    moving: true,
    moving_closings: true,
    foreman: true,
    driver_shifts: true,
    helpers: true,
  },
  // TimeClock Pro - time tracking only
  standalone_timeclock: {
    dashboard: false,
    shipments: false,
    dispatch: false,
    drivers: false,
    tracking: false,
    messaging: false,
    accounting: false,
    accounting_drivers: false,
    accounting_rates: false,
    accounting_worksheets: false,
    hr: true,
    clock: true,
    geofencing: true,
    pto: true,
    master_tables: false,
    users: true,
    ai_parsing: false,
  },
  // PayTrack - accounting with rate sheets for Owner Ops and Per Trip
  standalone_accounting: {
    dashboard: false,
    shipments: false,
    dispatch: false,
    drivers: false,
    tracking: false,
    messaging: false,
    accounting: true,
    accounting_drivers: false,  // No hourly drivers (requires clock in/out)
    accounting_rates: true,     // Rate sheet parsing for Owner Ops
    accounting_worksheets: true, // Per trip driver worksheets
    accounting_manual: true,    // Manual timesheet entry
    hr: false,
    clock: false,
    geofencing: false,
    pto: false,
    master_tables: false,
    users: true,
    ai_parsing: true,           // AI parsing for rate sheets
  },
  // Free/No subscription - limited access
  none: {
    dashboard: true,
    shipments: true,
    dispatch: false,
    drivers: false,
    tracking: false,
    messaging: false,
    accounting: false,
    accounting_drivers: false,
    accounting_rates: false,
    accounting_worksheets: false,
    hr: false,
    clock: false,
    geofencing: false,
    pto: false,
    master_tables: false,
    users: false,
    ai_parsing: false,
  }
};

// Navigation items for each subscription type
const NAVIGATION_CONFIG = {
  full: [
    { id: 'dashboard', label: 'nav.dashboard', icon: 'LayoutDashboard', path: '/dashboard' },
    { id: 'master', label: 'nav.masterTables', icon: 'Database', path: '/master' },
    { id: 'messaging', label: 'nav.messaging', icon: 'MessageSquareText', path: '/messaging' },
    { id: 'tracking', label: 'nav.gps', icon: 'MapPin', path: '/tracking' },
    { id: 'accounting', label: 'nav.accounting', icon: 'Calculator', path: '/accounting' },
    { id: 'users', label: 'nav.users', icon: 'Users', path: '/users' },
  ],
  standalone_timeclock: [
    { id: 'timeclock', label: 'nav.timeclockPro', icon: 'Clock', path: '/timeclock' },
    { id: 'hr', label: 'nav.accounting', icon: 'Calendar', path: '/accounting?tab=hr' },
    { id: 'users', label: 'nav.users', icon: 'Users', path: '/users' },
  ],
  standalone_accounting: [
    { id: 'paytrack', label: 'nav.paytrack', icon: 'Calculator', path: '/paytrack' },
    { id: 'users', label: 'nav.users', icon: 'Users', path: '/users' },
  ],
  none: [
    { id: 'dashboard', label: 'nav.dashboard', icon: 'LayoutDashboard', path: '/dashboard' },
  ]
};

// Module-to-feature mapping: maps module IDs to subscription feature flags
const MODULE_FEATURE_MAP = {
  core: ['dashboard', 'shipments', 'dispatch', 'drivers', 'messaging', 'master_tables', 'users', 'system', 'terminal_integration', 'gate_schedules', 'pod'],
  port_intelligence: ['tracking'],
  port_intelligence_pro: ['tracking'],
  dnd_dispute_dashboard: ['dnd_disputes'],
  port_intelligence_suite: ['tracking', 'dnd_disputes'],
  accounting: ['accounting', 'accounting_drivers', 'accounting_rates', 'accounting_worksheets'],
  otr: ['otr', 'load_board', 'hos'],
  ai_automation: ['ai_parsing', 'freight_margin_ai'],
  timeclock: ['clock', 'hr', 'geofencing', 'pto'],
  compliance: ['compliance', 'equipment_issues'],
  broker_tms: ['broker_tms'],
  roadside_rescue: ['roadside_rescue'],
  moving_closings: ['moving', 'moving_closings', 'foreman', 'driver_shifts', 'helpers'],
  marketplace: [],
};

export const SubscriptionProvider = ({ children }) => {
  const { api, user, isAuthenticated, isSuperAdmin } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [companyModules, setCompanyModules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState(SUBSCRIPTION_FEATURES.none);
  const [testTier, setTestTier] = useState(null); // For super admin tier testing

  // Compute effective features from subscription tier AND module assignments
  const computeEffectiveFeatures = useCallback((subType, modules) => {
    // Start with the tier's feature set
    const tierFeatures = SUBSCRIPTION_FEATURES[subType] || SUBSCRIPTION_FEATURES.full;
    
    // If no module restrictions (modules is null = no explicit assignment), use tier features as-is
    if (!modules || modules.length === 0) {
      return tierFeatures;
    }

    // Build allowed features from assigned modules
    const allowedFeatures = new Set();
    modules.forEach(moduleId => {
      const mapped = MODULE_FEATURE_MAP[moduleId] || [];
      mapped.forEach(f => allowedFeatures.add(f));
    });

    // Intersect: only enable features that are BOTH in the tier AND in assigned modules
    const effective = {};
    Object.keys(tierFeatures).forEach(featureKey => {
      // If the feature is in the tier AND (allowed by modules OR not mapped to any module)
      const isMapped = Object.values(MODULE_FEATURE_MAP).flat().includes(featureKey);
      effective[featureKey] = tierFeatures[featureKey] && (!isMapped || allowedFeatures.has(featureKey));
    });

    return effective;
  }, []);

  // Set test tier (super admin only)
  const setTestSubscriptionTier = useCallback((tier) => {
    if (isSuperAdmin) {
      setTestTier(tier);
      if (tier && SUBSCRIPTION_FEATURES[tier]) {
        setFeatures(SUBSCRIPTION_FEATURES[tier]);
      } else {
        // Reset to full when exiting test mode
        setFeatures(SUBSCRIPTION_FEATURES.full);
      }
    }
  }, [isSuperAdmin]);

  // Exit tier test mode
  const exitTierTestMode = useCallback(() => {
    setTestTier(null);
    setFeatures(SUBSCRIPTION_FEATURES.full);
  }, []);

  const fetchSubscription = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setSubscription(null);
      setCompanyModules(null);
      setFeatures(SUBSCRIPTION_FEATURES.none);
      setLoading(false);
      return;
    }

    // Super admin has full access (unless testing a tier)
    if (user.role === 'super_admin') {
      setSubscription({ subscription_type: testTier || 'full', active: true });
      setCompanyModules(null);
      setFeatures(SUBSCRIPTION_FEATURES[testTier] || SUBSCRIPTION_FEATURES.full);
      setLoading(false);
      return;
    }

    try {
      // Fetch both subscription AND module assignments in parallel
      const [subResponse, modResponse] = await Promise.allSettled([
        api.get('/payments/subscription'),
        api.get('/admin/my-modules')
      ]);

      const subData = subResponse.status === 'fulfilled' ? subResponse.value.data : null;
      const modData = modResponse.status === 'fulfilled' ? modResponse.value.data : null;

      setSubscription(subData);
      setCompanyModules(modData);

      // Determine subscription type
      const subType = subData?.subscription_type || 'full';
      
      // If modules have bypass_payment enabled, treat as full access (demo/internal companies)
      const effectiveSubType = modData?.bypass_payment ? 'full' : subType;
      
      // For standalone subscriptions, enforce tier restrictions (modules don't apply)
      if (effectiveSubType.startsWith('standalone_')) {
        if (subData?.active) {
          setFeatures(SUBSCRIPTION_FEATURES[subType] || SUBSCRIPTION_FEATURES.full);
        } else {
          setFeatures(SUBSCRIPTION_FEATURES.none);
        }
      } else {
        // Full subscription: intersect tier features with module assignments
        const assignedModules = modData?.modules || null;
        const effective = computeEffectiveFeatures(effectiveSubType, assignedModules);
        setFeatures(effective);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      // Default to full features if subscription check fails (for existing users)
      setFeatures(SUBSCRIPTION_FEATURES.full);
    } finally {
      setLoading(false);
    }
  }, [api, isAuthenticated, user, testTier, computeEffectiveFeatures]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Check if a specific feature is available
  const hasFeature = useCallback((featureId) => {
    return features[featureId] === true;
  }, [features]);

  // Check if user has any of the specified features
  const hasAnyFeature = useCallback((featureIds) => {
    return featureIds.some(id => features[id] === true);
  }, [features]);

  // Check if user has all of the specified features
  const hasAllFeatures = useCallback((featureIds) => {
    return featureIds.every(id => features[id] === true);
  }, [features]);

  // Get subscription type
  const getSubscriptionType = useCallback(() => {
    if (!subscription) return 'none';
    return subscription.subscription_type || 'full';
  }, [subscription]);

  // Get navigation items based on subscription
  const getNavigationItems = useCallback(() => {
    const subType = getSubscriptionType();
    return NAVIGATION_CONFIG[subType] || NAVIGATION_CONFIG.full;
  }, [getSubscriptionType]);

  // Check if this is a standalone subscription
  const isStandalone = useCallback(() => {
    const subType = getSubscriptionType();
    return subType.startsWith('standalone_');
  }, [getSubscriptionType]);

  // Get the standalone product name
  const getStandaloneProductName = useCallback(() => {
    const subType = getSubscriptionType();
    if (subType === 'standalone_timeclock') return i18n.t('modules.standalone_timeclock', 'TimeClock Pro');
    if (subType === 'standalone_accounting') return i18n.t('modules.standalone_accounting', 'PayTrack');
    return null;
  }, [getSubscriptionType]);

  const value = {
    subscription,
    companyModules,
    loading,
    features,
    hasFeature,
    hasAnyFeature,
    hasAllFeatures,
    getSubscriptionType,
    getNavigationItems,
    isStandalone,
    getStandaloneProductName,
    refreshSubscription: fetchSubscription,
    // Tier testing for super admin
    testTier,
    setTestSubscriptionTier,
    exitTierTestMode,
    isInTierTestMode: !!testTier,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
